import { type CookbookTypeV3 } from '@hungryroot/rest';
import { getRecipesPageName } from './getRecipesPageName';
import { analytics } from './segment';
import { type AccessedFrom } from './types/AccessedFrom';
import { type Action } from './types/Action';
import {
  buildFeedInteractionPayload,
  type FeedInteractionFields,
} from './types/FeedInteractionFields';
import { type Location } from './types/Location';
import { resolveInteractionTab } from './utils/resolveInteractionTab';

type PairingInteractionParams = FeedInteractionFields & {
  pairingId: number;
  pairingName?: string | null;
  actionType: Action;
  location?: Location | null;
  orderId?: number;
  pageName?: string;
  conversationId?: string;
  /** Optional: surface the user came from before triggering this interaction. */
  accessedFrom?: AccessedFrom;
  /** Optional: present when the interaction originated from a search result. */
  searchId?: string;
  /** Optional: 0-indexed position in the search result list. */
  resultPosition?: number;
  /** Optional: interaction type (e.g. 'view') for events that use interaction_type alongside action_type. */
  interactionType?: string;
  /** Optional: whether the pairing is coming soon (out of stock but planned for restock). */
  comingSoon?: boolean;
  /** Optional: whether the pairing is out of stock. `null` when unavailable at the call site. */
  outOfStock?: boolean | null;
  /** Cookbook the pairing belongs to (when interaction surface is a cookbook). */
  cookbookId?: number;
  /** Display name of the cookbook the pairing belongs to. */
  cookbookName?: string;
  /** Backend-provided cookbook classification, e.g. 'marketing' or 'favorites'. */
  cookbookType?: CookbookTypeV3;
  /** UI surface that contains the pairing. */
  componentType?: 'featured_cookbook' | 'carousel' | 'card';
  /** Identifier for the surface, e.g. 'featured_cookbook'. */
  componentName?: string;
  /** Layout the card was rendered in when the event fired. */
  componentLayout?: 'list' | 'grid';
  /** True when the pairing is rendered inside a "featured" surface. */
  featured?: boolean;
  /**
   * 0-indexed position of the pairing within its surface (e.g. carousel slide
   * index).
   */
  objectIndex?: number;
  /**
   * Max index of the surface — i.e. (total - 1). For a carousel of 13 items
   * this is 12. Mirrors `homepageDefaultPageSections.length - 1` semantics.
   */
  objectTotal?: number;
  /** Personalized badge labels rendered for this pairing in the UI. */
  personalizedBadges?: string[];
  /** Whether personalized badges were rendered for this pairing in the UI. */
  badgeShown?: boolean;
};

export const pairingInteraction = ({
  pairingId,
  pairingName,
  actionType,
  location,
  orderId,
  pageName,
  conversationId,
  accessedFrom,
  searchId,
  resultPosition,
  interactionType,
  comingSoon,
  outOfStock,
  cookbookId,
  cookbookName,
  cookbookType,
  componentType,
  componentName,
  componentLayout,
  featured,
  objectIndex,
  objectTotal,
  personalizedBadges,
  badgeShown,
  tab,
  tabSessionId,
  container,
  rankedListPosition,
  verticalPosition,
  horizontalPosition,
  contentType,
  recommendationSource,
  carouselId,
  carouselName,
  impressionId,
  cookbookOwnerId,
}: PairingInteractionParams) => {
  analytics.track('Pairing Interaction', {
    pairing_id: pairingId,
    pairing_name: pairingName,
    action_type: actionType,
    location,
    page_name: pageName ?? getRecipesPageName(),
    order_id: orderId,
    client: 'web',
    conversation_id: conversationId,
    accessed_from: accessedFrom,
    search_id: searchId,
    result_position: resultPosition,
    ...(interactionType !== undefined && { interaction_type: interactionType }),
    coming_soon: comingSoon,
    out_of_stock: outOfStock,
    cookbook_id: cookbookId,
    cookbook_name: cookbookName,
    cookbook_type: cookbookType,
    component_type: componentType,
    component_name: componentName,
    component_layout: componentLayout,
    featured,
    object_index: objectIndex,
    object_total: objectTotal,
    ...(badgeShown !== undefined && { badge_shown: badgeShown }),
    ...(personalizedBadges !== undefined && {
      personalized_badges: personalizedBadges,
    }),
    ...buildFeedInteractionPayload({
      tab: resolveInteractionTab(tab),
      tabSessionId,
      container,
      rankedListPosition,
      verticalPosition,
      horizontalPosition,
      contentType,
      recommendationSource,
      carouselId,
      carouselName,
      impressionId,
      cookbookOwnerId,
    }),
  });
};
